import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Buffer } from 'buffer';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import FormItem from 'components/form/FormItem';
import Loading from 'components/global/Loading';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useMediaQuery from 'hooks/useMediaQuery';

import {
	getPrintSettings,
	setIsUserSelectedLocale,
	setPrintLocale,
	setPrintSettings
} from 'pages/Settings/Print/PrintSettingsSlice';
import usePrintUtils from 'pages/Settings/Print/hooks/usePrintUtils';
import useQrCode from 'pages/Settings/Print/hooks/useQrCode';
import useSauidiQR from 'pages/Settings/Print/hooks/useSauidiQR';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { languages } from 'utils/localization';

import usePrintLocale from './usePrintLocale';

const PrintBranchLabel = NiceModal.create(({ ids, clientData = null, defaultLang }) => {
	// States
	const [orderData, setOrderData] = useState(null);
	// Print States
	const [templateName, setTemplateName] = useState(null);
	const [branchPrintSettings, setBranchPrintSettings] = useState(null);

	// Hooks
	const modal = useModal();
	const dispatch = useDispatch();
	const { generateQrCode } = useQrCode();
	const { generateBase64String } = useSauidiQR();
	const { fetchLanguage } = usePrintLocale();
	const { error, fetchOrderPrintData, handlePDFDownload, isLoading, pdfFile, mergePDF, renderPDFChunks } =
		usePrintUtils();
	const { t } = useTranslation();

	// Selectors && Static States
	const companySettings = useSelector((state) => state?.user?.generalSettings) ?? null;
	const { mobile = false } = useSelector((state) => state?.layout);
	const isMobileDevice = useMediaQuery(Breakpoints.mobile);

	const { printLocale, isUserSelectedLocale } = useSelector((state) => state?.printSettings);
	const langs = useMemo(() => languages.map((language) => language.code), []);

	const defaultColSpan = {
		md: 2
	};

	// Helpers
	const getPropJSON = useCallback(async () => {
		const printSettings = await dispatch(getPrintSettings()).unwrap();
		const branchPrintPropJSON = printSettings?.branchPrintPropJSON;

		if (!branchPrintPropJSON) return AppLogger.error('branchPrintPropJSON not found');

		setTemplateName(branchPrintPropJSON?.templateName);
		setBranchPrintSettings(branchPrintPropJSON);

		!isUserSelectedLocale && dispatch(setPrintLocale(branchPrintPropJSON?.language));
	}, [isUserSelectedLocale]);

	const handleLanguageChange = useCallback(
		async (value) => {
			dispatch(setPrintLocale(value)).then(() => {
				dispatch(setIsUserSelectedLocale(true));
				dispatch(
					setPrintSettings({
						branchPrintPropJSON: { ...branchPrintSettings, language: value }
					})
				);
			});
		},
		[branchPrintSettings]
	);

	const createPDF = async (orderData) => {
		if (!orderData) return;

		await mergePDF(async (merger) => {
			const mergeQueue = [];
			for await (const data of orderData) {
				let saudiQRData;

				if (window.ReactNativeWebView) {
					if (!clientData) return AppLogger.error('generateQRDataArr : clientData not found external print');
					saudiQRData = generateBase64String(data, clientData);
				} else {
					saudiQRData = generateBase64String(data, {
						ClientName: companySettings?.ClientName ?? '',
						VATNumber: companySettings?.VATNumber ?? '0000000000'
					});
				}

				const qrCode = await generateQrCode(saudiQRData, 200);
				mergeQueue.push({
					propJSON: branchPrintSettings,
					companyLogo: companySettings?.CompanyLogo ?? null,
					fieldName: 'branchPrintPropJSON',
					qrCode,
					...data,
					key: data?.OrderID
				});
			}

			const blobs = await renderPDFChunks(mergeQueue);
			for (const pdfBlob of blobs) {
				await merger.add(pdfBlob);
			}

			if (window.ReactNativeWebView) {
				const buffer = await merger.saveAsBuffer();
				const u8 = new Uint8Array(buffer);
				const b64 = Buffer.from(u8).toString('base64');
				window.ReactNativeWebView.postMessage(b64);
			} else {
				const mergedPdf = await merger.saveAsBlob();
				return mergedPdf;
			}
		});
	};

	const getPrintData = useCallback(async (ids) => {
		if (!ids) return;
		await getPropJSON();
		await fetchOrderPrintData(ids).then((data) => {
			if (data) {
				setOrderData(data);
			}
		});
	}, []);

	// Effects
	useEffect(() => {
		getPrintData(ids);
	}, [ids]);

	useEffect(() => {
		if (defaultLang) {
			dispatch(setPrintLocale(defaultLang));
		}
	}, [defaultLang]);

	useEffect(() => {
		setBranchPrintSettings((prevState) => ({
			...prevState,
			...(printLocale && { language: printLocale })
		}));
	}, [printLocale]);

	useEffect(() => {
		if (!branchPrintSettings) return;
		if (!orderData) return;

		if (branchPrintSettings.language !== printLocale) return;

		if (branchPrintSettings.templateName === 'template2' || branchPrintSettings.templateName === 'template5') {
			fetchLanguage([branchPrintSettings.language, 'arabicValue']).then(() => createPDF(orderData));
		} else {
			fetchLanguage(branchPrintSettings.language).then(() => createPDF(orderData));
		}
	}, [branchPrintSettings, orderData]);

	useEffect(() => {
		if (error) {
			modal.remove();
		}
	}, [error]);

	return (
		<>
			{!isLoading ? (
				<Modal closeButton maxWidth="x-large" fullwidth className="modal-printOrders">
					<ModalTitle>
						<Typography variant="p1">orderDetails</Typography>
						<FormItem colSpan={defaultColSpan}>
							<EnumSelect
								valueRemovable={false}
								data={['template1', 'template2', 'template3', 'template4', 'template5']}
								value={templateName}
								setValue={(value) => {
									setTemplateName(value);
									setBranchPrintSettings((prevState) => ({
										...prevState,
										templateName: value
									}));
								}}
								searchable={false}
							/>
						</FormItem>
						<FormItem colSpan={defaultColSpan}>
							<EnumSelect
								valueRemovable={false}
								data={langs}
								value={printLocale}
								setValue={(value) => {
									handleLanguageChange(value);
								}}
								searchable={false}
							/>
						</FormItem>
						{mobile && pdfFile && (
							<FormItem colSpan={defaultColSpan}>
								<Button
									fullWidth={true}
									onClick={() => handlePDFDownload(pdfFile, `Label-${ids.join('-')}`)}>
									{t('download')}
								</Button>
							</FormItem>
						)}
					</ModalTitle>
					{!isMobileDevice && (
						<ModalContent
							style={{
								height: '80dvh'
							}}>
							{pdfFile ? (
								<div
									style={{ height: '100%' }}
									dangerouslySetInnerHTML={{
										__html: `<iframe src='${pdfFile}' width='100%' height='100%' />`
									}}
								/>
							) : (
								<Loading />
							)}
						</ModalContent>
					)}
				</Modal>
			) : (
				<Loading />
			)}
		</>
	);
});

export default PrintBranchLabel;
